import React, { useEffect, useRef } from 'react';
import * as d3 from 'd3';
import theme from 'common/theme';

interface RunningGaugeChartProps {
    value?: number;
    target?: number;
    max?: number;
    isOnTrack?: boolean;
}

const RunningGaugeChart = ({ value = 0, target = 0, max = 100, isOnTrack }: RunningGaugeChartProps) => {
    const ref = useRef<SVGSVGElement | null>(null);

    const arcColor = isOnTrack ? theme.colors.success200 : theme.colors.error200;
    const targetLineColor = theme.colors.gray800;
    const needleColor = theme.colors.black;

    const ontrackText = isOnTrack ? 'On track' : 'Not On track';

    useEffect(() => {
        const width = 300;
        const height = 250;
        const arcRadius = 100; // Radius for the main arc
        const lowerArcRadius = arcRadius - 45; // Radius for the lower arc

        const centerX = width / 2;
        const centerY = height - 35; // Center Y with extra space above

        const svg = d3.select(ref.current)
            .attr('width', width)
            .attr('height', height);

        svg.selectAll('*').remove();

        // Main arc (top)
        const arc = d3.arc()
            .innerRadius(arcRadius - 20)
            .outerRadius(arcRadius)
            .startAngle(-Math.PI / 2)
            .endAngle(Math.PI / 2);

        svg.append('path')
            .attr('d', arc(undefined))
            .attr('fill', '#ddd')
            .attr('transform', `translate(${centerX}, ${centerY})`);

        // Lower arc (inside main arc)
        const lowerArc = d3.arc()
            .innerRadius(lowerArcRadius)
            .outerRadius(lowerArcRadius - 5)
            .startAngle(-Math.PI / 2)
            .endAngle(Math.PI / 2);

        svg.append('path')
            .attr('d', lowerArc(undefined))
            .attr('fill', '#bbb')
             .attr('transform', `translate(${centerX}, ${centerY})`);

        const scale = d3.scaleLinear()
            .domain([0, max])
            .range([-Math.PI / 2, Math.PI / 2]);

        // Foreground arc (main arc progress)
        const arcForeground = d3.arc()
            .innerRadius(arcRadius - 20)
            .outerRadius(arcRadius)
            .startAngle(-Math.PI / 2)
            .endAngle(scale(value));

        svg.append('path')
            .attr('d', arcForeground(undefined))
            .attr('fill', arcColor)
            .attr('transform', `translate(${centerX}, ${centerY})`);

        // Target line on the outer arc
        const targetAngle = scale(target);
        const targetElement = svg.append('g')
            .attr('transform', `translate(${centerX}, ${centerY}) rotate(${targetAngle * 180 / Math.PI})`);

        const innerTargetX = arcRadius - 20;
        const outerTargetX = arcRadius + 10;

        targetElement.append('line')
            .attr('x1', 0)
            .attr('y1', -innerTargetX)
            .attr('x2', 0)
            .attr('y2', -outerTargetX)
            .attr('stroke', targetLineColor)
            .attr('stroke-width', 2)
            .attr('stroke-dasharray', '4,3');

        // Diamond shape at the end of the target line
        const diamondSize = 4;
        targetElement.append('path')
            .attr('d', `M 0 ${-outerTargetX - diamondSize} L ${diamondSize} ${-outerTargetX} L 0 ${-outerTargetX + diamondSize} L ${-diamondSize} ${-outerTargetX} Z`)
            .attr('fill', targetLineColor);

        // Needle configuration
        const needleLength = arcRadius - 20; // Extend to the inner part of the outer arc
        const needleBaseRadius = lowerArcRadius; // Start from outside the lower arc
        const needleWidth = 4; // Width of the needle

        const needleAngle = scale(value); // Calculate angle based on value

        const needleElement = svg.append('g')
            .attr('transform', `translate(${centerX}, ${centerY}) rotate(${needleAngle * 180 / Math.PI})`);

        const topX = 0;
        const topY = -needleLength;

        const leftX = -needleWidth;
        const leftY = -needleBaseRadius;

        const rightX = needleWidth;
        const rightY = -needleBaseRadius;

        needleElement.append('path')
            .attr('class', 'needle')
            .attr('d', `M ${leftX} ${leftY} L ${topX} ${topY} L ${rightX} ${rightY} Z`)
            .attr('fill', needleColor);

        // Outer circle of the needle
        needleElement.append('circle')
            .attr('class', 'needle-center')
            .attr('cx', 0)
            .attr('cy', -needleBaseRadius)
            .attr('r', needleWidth)
            .attr('fill', needleColor);

        // Inner circle inside the needle circle
        needleElement.append('circle')
            .attr('class', 'needle-inner-circle')
            .attr('cx', 0)
            .attr('cy', -needleBaseRadius)
            .attr('r', (needleWidth - 2) / 2) // Smaller radius for the inner circle
            .attr('fill', theme.colors.white); // White color for the inner circle

        // Texts below the lower arc
        svg.append('text')
            .attr('x', centerX)
            .attr('y', centerY - 20)
            .attr('text-anchor', 'middle')
            .attr('font-size', '24px')
            .attr('fill', arcColor)
            .text(`${value}h`);

        svg.append('text')
            .attr('x', centerX)
            .attr('y', centerY)
            .attr('text-anchor', 'middle')
            .attr('font-size', '14px')
            .attr('fill', arcColor)
            .text(ontrackText);
    }, [value, target, max, isOnTrack, arcColor, ontrackText, needleColor]);

    return (
        <div className="gauge-chart-container">
            <svg ref={ref} className='guage-chart'></svg>
        </div>
    );
};

export default RunningGaugeChart;
